import { Injectable, inject } from '@angular/core';

import { select, Store } from '@ngrx/store';
import { take } from 'rxjs/operators';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { CurrentUserSelectors } from '@core/account-data';
import { filterBoolean } from '@core/common-legacy';
import { OfferKind, OfferStatus } from '@core/offers-types';
import type { Placement } from '@core/placements-types';

export type PlacementAction =
  | 'proceed'
  | 'disqualify'
  | 'requalify'
  | 'go_to_offer'
  | 'remove'
  | 'edit_hiring_details'
  | 'edit_preferred_work_locations'
  | 'open_more_dropdown';

@Injectable({ providedIn: 'root' })
export class PlacementActionsStrategyService {
  private readonly store = inject(Store);

  private canManageCandidates = false;
  private canViewOffers = false;
  private canViewAllOffers = false;
  private hasAccessToOfferIds: number[] = [];
  private hasAccessToPlacementIds: number[] = [];

  constructor() {
    this.store.pipe(select(AbilitiesSelectors.abilityMap), take(1)).subscribe(enabledMap => {
      this.canManageCandidates = enabledMap[Ability.ManageCandidates];
      this.canViewOffers = enabledMap[Ability.ViewJobs];
      this.canViewAllOffers = enabledMap[Ability.ViewAllJobs];
    });
    this.store
      .select(CurrentUserSelectors.data)
      .pipe(filterBoolean, take(1))
      .subscribe(user => {
        this.hasAccessToOfferIds = [
          ...(user.membership?.offerIds || []),
          ...(user.membership?.talentPoolIds || []),
        ];
        this.hasAccessToPlacementIds = user.membership?.accessiblePlacementIds ?? [];
      });
  }

  public getAvailable(placement: Placement): PlacementAction[] {
    const id = (placement.offerId ||
      placement.talentPoolId ||
      placement.offer?.id ||
      placement.talentPool?.id)!;
    const hasAccessToOffer = this.canViewAllOffers || this.hasAccessToOfferIds.includes(id);
    const hasAccessToPlacement = this.hasAccessToPlacementIds.includes(placement.id);

    if (!hasAccessToOffer && !hasAccessToPlacement) return [];

    const { offer, talentPool } = placement;
    const status = (offer?.status || talentPool?.status)!;
    const isJob = offer ? offer.kind === OfferKind.Job : false;
    const isOfferActive = status !== OfferStatus.Archived;
    const isDisqualified = !!placement.disqualifyReason;
    const isArchivedTalentPool = !isOfferActive && !isJob;

    const canProceed = this.canManageCandidates && isJob && isOfferActive && !isDisqualified;
    const canDisqualify = this.canManageCandidates && isOfferActive && !isDisqualified && isJob;
    const canRequalify = this.canManageCandidates && isOfferActive && isDisqualified && isJob;
    const canGoToOffer = this.canViewOffers && !isArchivedTalentPool;
    const canRemove = this.canManageCandidates;
    const canEditPreferredWorkLocations = this.canManageCandidates && isJob;
    const canOpenMoreDropdown = this.canManageCandidates && hasAccessToOffer;

    const actions: PlacementAction[] = [];

    if (canProceed) actions.push('proceed');
    if (canDisqualify) actions.push('disqualify');
    if (canRequalify) actions.push('requalify');
    if (canGoToOffer) actions.push('go_to_offer');
    if (canRemove) actions.push('remove');
    if (canEditPreferredWorkLocations) actions.push('edit_preferred_work_locations');
    if (canOpenMoreDropdown) actions.push('open_more_dropdown');

    return actions;
  }
}
